import { supabase } from '../lib/supabase';

/**
 * OTP Service for Status Confirmation
 * Handles OTP generation, verification, and status confirmation
 */

class OTPService {
  /**
   * Request OTP code for email verification
   * @param {string} email - User email
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async requestOTP(email) {
    try {
      // Step 1: Create OTP in database
      const { data, error } = await supabase?.rpc('create_otp_verification', {
        user_email: email
      });

      if (error) throw error;

      // Step 2: Send OTP email via Edge Function
      const otpData = data?.[0];
      if (otpData) {
        try {
          // Call Edge Function
          const response = await supabase?.functions?.invoke('send-otp-email', {
            body: {
              email: email,
              otpCode: otpData?.otp_code,
              expiresAt: otpData?.expires_at
            }
          });

          console.log('📧 Full Edge Function Response:', response);

          // Check for error
          if (response?.error) {
            console.error('Error sending OTP email:', response?.error);
            
            // Extract detailed error message
            let errorMessage = 'Email slanje neuspešno';
            
            // The response.data contains the error details even when there's an error
            const errorBody = response?.data;
            
            console.log('📧 Error Body:', errorBody);
            
            if (errorBody && typeof errorBody === 'object') {
              if (errorBody?.error) {
                errorMessage = errorBody?.error;
                
                // Add Resend-specific error
                if (errorBody?.resendError) {
                  errorMessage += ` - ${errorBody?.resendError}`;
                }
                
                // Add status code
                if (errorBody?.statusCode) {
                  errorMessage += ` (Status: ${errorBody?.statusCode})`;
                }
                
                // Add hint
                if (errorBody?.hint) {
                  errorMessage += ` - ${errorBody?.hint}`;
                }
              }
            } else if (response?.error?.message) {
              errorMessage = response?.error?.message;
            }
            
            throw new Error(errorMessage);
          }
          
          // Check if response indicates failure
          if (response?.data && !response?.data?.success) {
            const errorMsg = response?.data?.error || 'Email slanje neuspešno';
            const hint = response?.data?.hint ? ` - ${response?.data?.hint}` : '';
            throw new Error(errorMsg + hint);
          }

          // Success
          console.log('✅ Email sent successfully:', response?.data?.emailId);
        } catch (emailErr) {
          console.error('Failed to invoke email function:', emailErr);
          // Re-throw with clear message
          throw new Error(`Greška pri slanju email-a: ${emailErr?.message || 'Nepoznata greška'}`);
        }
      }

      return {
        success: true,
        data: {
          message: 'OTP kod je poslat na vaš email',
          expiresAt: otpData?.expires_at
        }
      };
    } catch (error) {
      console.error('Error requesting OTP:', error);
      return {
        success: false,
        error: {
          message: error?.message || 'Greška pri slanju OTP koda'
        }
      };
    }
  }

  /**
   * Verify OTP code
   * @param {string} email - User email
   * @param {string} otpCode - 6-digit OTP code
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async verifyOTP(email, otpCode) {
    try {
      const { data, error } = await supabase?.rpc('verify_otp_code', {
        user_email: email,
        code: otpCode
      });

      if (error) throw error;

      if (!data) {
        return {
          success: false,
          error: {
            message: 'Nevažeći ili istekao OTP kod'
          }
        };
      }

      return {
        success: true,
        data: {
          verified: true,
          message: 'OTP kod je uspešno verifikovan'
        }
      };
    } catch (error) {
      console.error('Error verifying OTP:', error);
      return {
        success: false,
        error: {
          message: error?.message || 'Greška pri verifikaciji OTP koda'
        }
      };
    }
  }

  /**
   * Confirm user status after OTP verification
   * @param {string} email - User email
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async confirmStatus(email) {
    try {
      const { data, error } = await supabase?.rpc('confirm_user_status_by_email', {
        user_email: email
      });

      if (error) throw error;

      // Check if any row was updated
      if (!data) {
        return {
          success: false,
          error: {
            message: 'Korisnik sa ovim email-om nije pronađen'
          }
        };
      }

      return {
        success: true,
        data: {
          confirmed: true,
          message: 'Status je uspešno potvrđen'
        }
      };
    } catch (error) {
      console.error('Error confirming status:', error);
      return {
        success: false,
        error: {
          message: error?.message || 'Greška pri potvrđivanju statusa'
        }
      };
    }
  }

  /**
   * Check if user has confirmed status
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async checkStatusConfirmation() {
    try {
      const { data, error } = await supabase?.rpc('has_confirmed_status');

      if (error) throw error;

      return {
        success: true,
        data: {
          hasConfirmedStatus: data || false
        }
      };
    } catch (error) {
      console.error('Error checking status confirmation:', error);
      return {
        success: false,
        error: {
          message: error?.message || 'Greška pri proveri statusa'
        }
      };
    }
  }
}

export const otpService = new OTPService();